/**
 * Adapts VoxelWorld to the shared physics engine's VoxelPhysicsWorld interface.
 *
 * Kept as its own file rather than a VoxelWorld method so VoxelWorld stays ignorant of
 * physics, and so the tri-state contract has exactly one place to be explained:
 *
 *   - LOADED     → the chunk's LIVE `data` array. Edits (`drawToChunk`) mutate it in place,
 *                  so collision is correct the instant an edit commits, with no remesh and
 *                  no invalidation. This is the whole point of colliding against voxels.
 *   - KNOWN_AIR  → `isEmptyAir`: genuine open sky above a column's known content top.
 *                  Traversable with no data, so falling from a high spawn works.
 *   - UNKNOWN    → everything else (never requested, in flight, or beyond the tile
 *                  frontier). The engine refuses to move the capsule into it.
 *
 * `null` here must always mean UNKNOWN, never air — returning air for an unloaded chunk is
 * precisely the fall-through-the-world bug this engine exists to remove.
 *
 * Reads COMMITTED `chunk.data` only, never `chunk.tempData`: build previews are a visual
 * overlay and must not collide.
 */

import { chunkKey, type VoxelPhysicsWorld } from '@worldify/shared';
import type { VoxelWorld } from './VoxelWorld';

export function createPhysicsWorldSource(world: VoxelWorld): VoxelPhysicsWorld {
  return {
    getChunkData(cx: number, cy: number, cz: number): Uint32Array | null {
      return world.getChunk(cx, cy, cz)?.data ?? null;
    },
    isKnownAir(cx: number, cy: number, cz: number): boolean {
      return world.isEmptyAir(cx, cy, cz);
    },
  };
}

/** Chunk key helper re-exported so callers wiring invalidation don't re-derive it. */
export { chunkKey };
